import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Card, CardContent } from '../components/ui/card';
import { Lightbulb, TrendingUp, Globe2, Database, BarChart3, Recycle, GraduationCap, CheckCircle2, ArrowRight } from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      id: 'strategy',
      icon: <Lightbulb className="w-8 h-8" />,
      title: 'Strategy & Advisory',
      description: 'Strategic frameworks and governance models for sustainable transition',
      color: 'bg-red-600',
      services: [
        'Sustainable tourism & destination strategies',
        'Governance models and institutional frameworks',
        'Carrying capacity assessments',
        'Policy-to-implementation frameworks',
        'Strategic planning and roadmaps',
        'Stakeholder consultation processes',
      ],
    },
    {
      id: 'climate',
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Climate & Energy',
      description: 'Decarbonisation and resilience strategies for sustainable energy systems',
      color: 'bg-red-600',
      services: [
        'Climate risk & resilience assessments',
        'Net Zero strategies and pathways',
        'Energy efficiency audits & ESCO services',
        'Decarbonisation roadmaps',
        'Renewable energy integration',
        'Climate adaptation planning',
      ],
    },
    {
      id: 'eu-projects',
      icon: <Globe2 className="w-8 h-8" />,
      title: 'EU Projects & Innovation',
      description: 'Expert consortium building and project leadership for EU-funded initiatives',
      color: 'bg-red-600',
      services: [
        'Consortium building and partner identification',
        'Work package design and leadership',
        'Impact assessment and evaluation',
        'Replication and scaling strategies',
        'Horizon Europe & Interreg expertise',
        'Innovation and pilot project development',
      ],
    },
    {
      id: 'digital',
      icon: <Database className="w-8 h-8" />,
      title: 'Digital & Data Systems',
      description: 'AI-powered platforms and IoT systems for sustainability monitoring',
      color: 'bg-red-600',
      services: [
        'AI tools and machine learning applications',
        'IoT monitoring systems deployment',
        'Sustainability dashboards & analytics',
        'Data platforms and integration',
        'Decision-support systems',
        'Predictive modeling and forecasting',
      ],
    },
    {
      id: 'esg',
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'ESG, Carbon & Compliance',
      description: 'Comprehensive carbon accounting and regulatory compliance solutions',
      color: 'bg-red-600',
      services: [
        'Carbon accounting (Scope 1, 2, 3)',
        'MRV (Monitoring, Reporting, Verification) systems',
        'EU regulation compliance (CSRD, SFDR, Taxonomy)',
        'ESG reporting and frameworks',
        'Anti-greenwashing verification',
        'Sustainability disclosure support',
      ],
    },
    {
      id: 'circular',
      icon: <Recycle className="w-8 h-8" />,
      title: 'Circular Economy & Food Systems',
      description: 'Sustainable gastronomy and circular supply chain solutions',
      color: 'bg-red-600',
      services: [
        'Sustainable gastronomy strategies',
        'Food waste reduction programs',
        'Circular supply chain design',
        'Local sourcing and procurement',
        'Farm-to-table initiatives',
        'Circular business model development',
      ],
    },
    {
      id: 'training',
      icon: <GraduationCap className="w-8 h-8" />,
      title: 'Training & Capacity Building',
      description: 'Professional development and stakeholder engagement programs',
      color: 'bg-red-600',
      services: [
        'Professional training workshops',
        'Capacity building programs',
        'Stakeholder engagement facilitation',
        'Knowledge transfer initiatives',
        'Educational materials development',
        'Train-the-trainer programs',
      ],
    },
  ];

  const approach = [
    {
      step: '01',
      title: 'Assessment',
      description: 'Evidence-based analysis of current state and opportunities',
    },
    {
      step: '02',
      title: 'Strategy',
      description: 'Co-creation of tailored solutions aligned with policy frameworks',
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'Technical execution with digital tools and systems integration',
    },
    {
      step: '04',
      title: 'Impact',
      description: 'Monitoring, evaluation, and continuous improvement',
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71"
            alt="Analytics"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
            <p className="text-2xl text-gray-200 leading-relaxed mb-8">
              Comprehensive solutions from strategy to implementation—aligned with EU policy frameworks and international standards
            </p>
            <p className="text-lg text-gray-300">
              Strategic advisory · Technical expertise · Digital innovation
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Our Approach</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic methodology from assessment to measurable impact
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {approach.map((item, idx) => (
              <div key={item.step} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                  <div className="text-6xl font-bold text-red-600/20 mb-4">{item.step}</div>
                  <h3 className="text-2xl font-bold text-black mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
                {idx < approach.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-red-600" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Accordion */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Service Categories</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Integrated expertise across seven core service areas
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {services.slice(0, 6).map((service) => (
              <Card key={service.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <CardContent className="p-0">
                  <div className={`bg-gradient-to-r ${service.color} p-8 text-white`}>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        {service.icon}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">{service.title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-100">{service.description}</p>
                  </div>
                  <div className="p-8">
                    <ul className="space-y-3">
                      {service.services.map((item, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Centered 7th card */}
          <div className="flex justify-center mt-8">
            <div className="w-full lg:w-1/2">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <CardContent className="p-0">
                  <div className={`bg-gradient-to-r ${services[6].color} p-8 text-white`}>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        {services[6].icon}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">{services[6].title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-100">{services[6].description}</p>
                  </div>
                  <div className="p-8">
                    <ul className="space-y-3">
                      {services[6].services.map((item) => (
                        <li key={item} className="flex items-start space-x-3">
                          <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">Why Choose Our Services</h2>
              <div className="space-y-6">
                {[
                  'EU policy expertise and regulatory compliance',
                  'Evidence-based, data-driven methodologies',
                  'Scalable and replicable solutions',
                  'Multi-stakeholder engagement processes',
                  'Technical excellence with digital innovation',
                  'Proven track record in EU-funded projects',
                ].map((item) => (
                  <div key={item} className="flex items-start space-x-4 p-4 rounded-lg bg-gradient-to-r from-gray-50 to-red-50">
                    <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-800 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Professional Partnership"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-10 text-gray-100">
            Discover how our services can support your sustainability objectives
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-10 py-6">
              Contact Us Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
